import { ArrowRight, MessageCircleQuestion } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { ProofCard } from "@/components/landing/ProofCard";
import { SessionPreview } from "@/components/landing/SessionPreview";
import { Footer } from "@/components/layout/Footer";
import { Header } from "@/components/layout/Header";
import { usePricingCopy } from "@/lib/pricingContext";
import { CaseStudyShowcase } from "@/components/marketing/CaseStudyShowcase";

interface FaqItem {
  q: string;
  a: ReactNode;
}

const FAQ_TEASER: FaqItem[] = [
  {
    q: "Is this a lawyer?",
    a: (
      <>
        No. We help you draft a letter you sign and send yourself. For complex cases involving
        litigation, we recommend a healthcare attorney.
      </>
    ),
  },
  {
    q: "What if my insurer denies the appeal?",
    a: (
      <>
        Many denials can be escalated to external review. We&apos;ll generate that letter too,
        when it&apos;s the right move.
      </>
    ),
  },
  {
    q: "Do you store my medical records?",
    a: (
      <>
        No. Uploads, extracted data, and drafted letters are all automatically deleted within 24
        hours — download links stay valid for 24 hours after delivery, so save your letter when
        you export it. See our{" "}
        <Link to="/hipaa" className="text-primary underline-offset-2 hover:underline">
          HIPAA notice
        </Link>
        .
      </>
    ),
  },
];

export function HowItWorks() {
  const { paidPriceLabel } = usePricingCopy();
  return (
    <div className="flex min-h-screen flex-col bg-bg text-ink">
      <Header />
      <main className="flex-1">
        <section className="border-b border-line bg-parchment py-16 sm:py-20">
          <div className="mx-auto grid max-w-[1180px] grid-cols-1 items-start gap-14 px-6 sm:px-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div>
              <span className="eyebrow">How it works</span>
              <h1 className="mt-7 max-w-[760px] font-serif text-[clamp(2rem,5vw,3rem)] font-normal leading-[1.08] tracking-[-0.02em] text-ink">
                Three steps. Twenty minutes.{" "}
                <em className="italic text-ink-muted">One PDF you can send.</em>
              </h1>
              <p className="mt-5 max-w-[640px] text-[16px] leading-[1.65] text-ink-soft sm:text-[17px]">
                Upload your denial letter. Our specialists research your case against your plan,
                CMS, and clinical guidelines. You get a fully drafted appeal — every claim cited to
                a real source — that you can sign and mail today.
              </p>
            </div>
            <ProofCard />
          </div>
        </section>
        <HeroCta />

        <SessionPreview />

        {/* ── Section 3: The three case studies (load-bearing) ────── */}
        <CaseStudyShowcase />

        {/* Page-level disclaimer per plan 10 §4a — sits between the
            showcase and the pricing comparison so it reads as a footer
            for the illustrative-examples block. */}
        <section className="border-t border-border bg-surface py-6">
          <div className="wrap">
            <div className="rounded-lg border border-border bg-bg/60 px-5 py-4 text-sm leading-relaxed text-muted">
              Examples on this page are illustrative scenarios constructed to demonstrate the
              product. They do not depict real patients or specific insurance company decisions.
              Probability scores shown are the model&apos;s estimates for the scenario described;
              actual results vary case-by-case and no outcome is guaranteed. We are not a law firm
              and do not provide legal advice — see our{" "}
              <Link to="/terms" className="text-primary underline-offset-2 hover:underline">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/hipaa" className="text-primary underline-offset-2 hover:underline">
                HIPAA notice
              </Link>
              .
            </div>
          </div>
        </section>

        {/* ── Verification story: the answer to "did the AI make this
            up?" — surfaced BEFORE payment, not just in-session. ───── */}
        <section className="bg-bg py-20" aria-labelledby="how-verification">
          <div className="wrap max-w-5xl">
            <div className="mb-10 text-center">
              <span className="eyebrow">No unverified claims</span>
              <h2
                id="how-verification"
                className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl"
              >
                How we verify every citation
              </h2>
              <p className="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-muted">
                AI can invent plausible-sounding policy language. Revetra is built so that an
                invented claim cannot reach your letter.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              <VerifyStep
                step="1"
                heading="Every claim cites a source"
                body="Each paragraph's [1], [2] markers point to a real passage we indexed — your plan's policy language, CMS guidelines, or clinical criteria. Citations trace back to sources you can read, not to the model's memory."
              />
              <VerifyStep
                step="2"
                heading="A second model checks each one"
                body="An independent verification pass compares every cited sentence against its source passage. If a source contradicts a claim, the section is rolled back and redrafted — not patched over."
              />
              <VerifyStep
                step="3"
                heading="Export is blocked until it all passes"
                body="Every section must pass citation verification before the letter can be exported. The references list shows a verified check per citation, so you can see exactly what your appeal stands on before you send it."
              />
            </div>
          </div>
        </section>

        {/* ── Section 4: What you get when you pay ────────────────── */}
        <section className="bg-bg py-20" aria-labelledby="how-what-you-get">
          <div className="wrap max-w-5xl">
            <div className="mb-10 text-center">
              <span className="eyebrow">Free vs paid</span>
              <h2
                id="how-what-you-get"
                className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-ink sm:text-4xl"
              >
                What you get when you pay
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <ValueColumn
                label="Free"
                heading="See whether this fight is worth picking"
                body="A probability score, a plain-English explanation of the denial, and a list of the policy criteria your case might cite — so you know what you're up against before you commit."
                tone="muted"
              />
              <ValueColumn
                label={`Paid · ${paidPriceLabel}`}
                heading="A complete, mailable appeal letter"
                body="Everything in Free, plus a fully drafted appeal letter (PDF + Word), every claim cited to a real source and verified before export, a submission guide for getting it to your payer, and the option to refine any paragraph before downloading."
                tone="primary"
              />
            </div>
          </div>
        </section>

        {/* ── Section 5: FAQ teaser ───────────────────────────────── */}
        <section className="border-t border-border bg-surface/30 py-20" aria-labelledby="how-faq">
          <div className="wrap max-w-3xl">
            <div className="mb-8 flex items-baseline justify-between gap-4">
              <h2
                id="how-faq"
                className="font-heading text-2xl font-extrabold tracking-tight text-ink sm:text-3xl"
              >
                Frequently asked
              </h2>
              <Link
                to="/faq"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
              >
                See all
                <ArrowRight size={14} aria-hidden />
              </Link>
            </div>
            <dl className="space-y-4">
              {FAQ_TEASER.map((item) => (
                <div
                  key={item.q}
                  className="rounded-lg border border-border bg-surface px-5 py-4"
                >
                  <dt className="flex items-center gap-2 font-display text-sm font-semibold text-ink">
                    <MessageCircleQuestion
                      size={14}
                      strokeWidth={1.75}
                      className="text-primary"
                      aria-hidden
                    />
                    {item.q}
                  </dt>
                  <dd className="mt-2 text-sm leading-relaxed text-muted">{item.a}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-12 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Link to="/session" className="btn-indigo inline-flex items-center gap-2 px-5 py-3">
                <span>Start your appeal</span>
                <ArrowRight size={16} aria-hidden />
              </Link>
              <Link to="/pricing" className="btn-glass px-5 py-3">
                See pricing
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function HeroCta() {
  // Sits in the negative space immediately under the dark hero, before
  // the first content section. Mirrors the landing page's primary CTA
  // placement so users coming from cold ads have one click to /session.
  return (
    <div className="border-b border-border bg-bg">
      <div className="wrap flex flex-col items-center justify-center gap-3 py-6 sm:flex-row">
        <Link to="/session" className="btn-indigo inline-flex items-center gap-2 px-5 py-3">
          <span>Start your appeal</span>
          <ArrowRight size={16} aria-hidden />
        </Link>
        <Link to="/pricing" className="btn-glass px-5 py-3">
          See pricing
        </Link>
      </div>
    </div>
  );
}

function VerifyStep({ step, heading, body }: { step: string; heading: string; body: string }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-6">
      <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-ink font-mono text-[13px] text-parchment">
        {step}
      </span>
      <h3 className="mt-4 font-heading text-lg font-bold tracking-tight text-ink">{heading}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted">{body}</p>
    </div>
  );
}

function ValueColumn({
  label,
  heading,
  body,
  tone,
}: {
  label: string;
  heading: string;
  body: string;
  tone: "muted" | "primary";
}) {
  const isPrimary = tone === "primary";
  return (
    <div
      className={`relative overflow-hidden rounded-xl border p-6 ${
        isPrimary
          ? "border-ink/40 bg-surface ring-1 ring-ink/10"
          : "border-line bg-surface"
      }`}
    >
      {isPrimary && (
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-primary to-accent"
        />
      )}
      <p
        className={`text-[10px] font-bold uppercase tracking-[0.18em] ${
          isPrimary ? "text-primary" : "text-muted"
        }`}
      >
        {label}
      </p>
      <h3 className="mt-2 font-heading text-xl font-bold tracking-tight text-ink">{heading}</h3>
      <p className="mt-3 text-sm leading-relaxed text-muted">{body}</p>
    </div>
  );
}
